import {
  Component,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  inject,
  linkedSignal,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { NotificationsService } from '../../../../services/notifications.service';
import { CurrentUserService } from '../../../../services/current-user.service';
import { InteractionBarStateService } from '../../../../services/interaction-bar-state.service';
import { StorageService } from '../../../../services/storage.service';
import { FloatingInputComponent } from '../../../inputs/floating-input/floating-input.component';
import { DropdownComponent } from '../../../inputs/dropdown/dropdown.component';
import { SimpleButtonComponent } from '../../../buttons/simple-medium-button/simple-button.component';
import { submittalEntrySidebarActions } from './submittal-entry-sidebar.actions';
import { LoadingSpinnerComponent } from '@app/framework/loading-animation/loading-spinner.component';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { submittalsActions } from '@app/store/submittals/submittals.actions';
import { SPEC_SECTION_REGEX } from '@app/store/submittals/submittals.constants';
import { submittalsSelectors } from '@app/store/submittals/submittals.selectors';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';
import { SubmittalRegistryPayloadDto } from '@app/store/submittals/submittals.types';

@Component({
  selector: 'app-submittal-entry-sidebar',
  templateUrl: './submittal-registry-entry-sidebar.component.html',
  styleUrls: ['./submittal-registry-entry-sidebar.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    FloatingInputComponent,
    DropdownComponent,
    SimpleButtonComponent,
    LoadingSpinnerComponent,
    SidebarHeaderComponent,
  ],
})
export class SubmittalRegistryEntrySidebarComponent implements OnInit, OnDestroy {
  private actions$ = inject(Actions);
  protected store = inject(Store);
  protected notif = inject(NotificationsService);
  protected storage = inject(StorageService);
  protected user = inject(CurrentUserService);
  protected interactionBar = inject(InteractionBarStateService);

  readonly SPEC_SECTION_REGEX = SPEC_SECTION_REGEX;

  @ViewChild('submittalEntryForm') submittalEntryForm: NgForm;

  model = linkedSignal<SubmittalRegistryPayloadDto>(() => {
    return {
      project_id: this.projectId(),
      custom_id: this.originalRegistryId(),
      spec_section: '',
      spec_description: '',
      item_description: '',
      owner_id: null,
    };
  });

  canEditRegistryId = false;

  nextRegistryIdIsLoading = toSignal(
    this.store.select(submittalsSelectors.selectNextSubmittalRegistryIdIsLoading),
  );
  originalRegistryId = toSignal(
    this.store.select(submittalsSelectors.selectNextSubmittalRegistryId),
  );
  invitedSPs = toSignal(this.store.select(viewProjectSelectors.selectInvitedServiceProviders));
  isSending = signal(false);
  projectId = toSignal(this.store.select(viewProjectSelectors.selectProjectId));

  isDestroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.store.dispatch(submittalEntrySidebarActions.loadPage());

    this.actions$
      .pipe(
        ofType(
          submittalsActions.createSubmittalRegistrySuccess,
          submittalsActions.createSubmittalRegistryFailure,
        ),
        takeUntil(this.isDestroyed$),
      )
      .subscribe(() => {
        this.isSending.set(false);
        this.interactionBar.close();
      });
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  updateModel(field: keyof SubmittalRegistryPayloadDto, value: any) {
    this.model.update((oldModel) => ({ ...oldModel, [field]: value }));
  }

  save() {
    if (!this.submittalEntryForm.valid) {
      this.submittalEntryForm.form.markAllAsTouched();
      this.notif.showError('Please make sure all fields are filled out correctly.');
      return;
    }

    this.isSending.set(true);

    this.store.dispatch(submittalsActions.createSubmittalRegistry({ registry: this.model() }));

    // Note: Close is driven by success notification/effect
  }

  onCanEditRegistryIdIconClick() {
    if (this.canEditRegistryId) {
      // if 'Revert' was clicked, reset value to original
      this.model.update((oldModel) => ({ ...oldModel, registry_id: this.originalRegistryId() }));
    }
    this.canEditRegistryId = !this.canEditRegistryId;
  }
}
